import { ReactNode, useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Button } from '@givebutter/primitives/button';
import { Dot } from '@givebutter/primitives/dot';
import { maskWholeNumber } from '@givebutter/util';
import {
    faBan,
    faCheck,
    faChevronRight,
    faEyes,
    faGhost,
    faPenToSquare,
    faTrashXmark,
    faUserPen,
} from '@fortawesome/pro-regular-svg-icons';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchAuctionBidders } from 'api/campaigns';
import * as DropdownMenu from 'components/dropdown-menu';
import { CopyContactInfo } from 'components/pages/campaign-settings/campaign-auction/CopyContactInfo';
import * as AnonymizeBidderDialog from 'components/pages/campaign-settings/campaign-auction/auction-bidders/AnonymizeBidderDialog';
import * as BanBidderDialog from 'components/pages/campaign-settings/campaign-auction/auction-bidders/BanBidderDialog';
import * as DeleteBidderDialog from 'components/pages/campaign-settings/campaign-auction/auction-bidders/DeleteBidderDialog';
import * as UpdateBidderNumberDialog from 'components/pages/campaign-settings/campaign-auction/bidders-index/UpdateBidderNumberDialog';
import { TableConstants, useReactTableState } from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import * as Tooltip from 'components/tooltip';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentAuction } from 'customHooks/useCurrentAuction';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { usePersistentSearch } from 'customHooks/usePersistentSearch';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { capitalize } from 'lodash';
import { DateTime } from 'luxon';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { twMerge } from 'tailwind-merge';
import { Bidder } from 'types/domain';

const columnHelper = createColumnHelper<Bidder>();

const useBiddersIndex = () => {
    const { pagination, setPagination, expanded, setExpanded } = useReactTableState();
    const { data: campaign } = useCurrentCampaign();
    const { data: auction } = useCurrentAuction();
    const campaignId = campaign.id;
    const auctionId = auction.id;
    const [search, setSearch] = usePersistentSearch();
    const searchTerm = useDebounce(search);
    const { auctionsManageBidder = false } = useFlags();
    const { accountId } = useCurrentAccount();

    const [isPending, params] = useIsPending(
        {
            page: pagination.pageIndex + 1,
            limit: pagination.pageSize,
            query: searchTerm ?? '',
        },
        500,
    );

    const { data: bidders } = useSuspenseQuery({
        queryKey: campaignQueryKeys.auctionBiddersWithFilters(campaign.id, auctionId, params),
        queryFn: () => fetchAuctionBidders(campaignId, auctionId, params),
    });

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: TableConstants.Expand,
                cell: info => {
                    const isExpanded = info.row.getIsExpanded();

                    return (
                        <Button
                            variant="none"
                            color="neutral"
                            aria-expanded={isExpanded}
                            aria-controls={'copy-contact-info'}
                            aria-label={'Contact Info'}
                            onClick={() => {
                                info.row.toggleExpanded(!isExpanded);
                            }}
                        >
                            <FontAwesomeIcon
                                className={`${isExpanded ? 'rotate-90' : ''}`}
                                icon={faChevronRight}
                            />
                        </Button>
                    );
                },
                meta: {
                    className: 'pr-0 w-8',
                },
            }),
            columnHelper.display({
                header: 'Name',
                cell: info => {
                    const {
                        first_name: firstName,
                        last_name: lastName,
                        created_at,
                    } = info.row.original;
                    const twoDaysAgo = DateTime.now().minus({ day: 2 });
                    const createdAt = DateTime.fromISO(created_at);
                    const isNew = createdAt > twoDaysAgo;

                    return (
                        <div className={'flex items-center gap-x-4'}>
                            <p className={'font-medium'}>{`${firstName} ${lastName}`}</p>
                            <p
                                className={`rounded-full bg-green-100 px-2 text-xs font-medium text-green-500 ${
                                    !isNew ? 'hidden' : ''
                                }`}
                            >
                                New
                            </p>
                        </div>
                    );
                },
            }),
            columnHelper.accessor('number', {
                header: 'Bidder ID #',
                enableSorting: false,
                cell: info => {
                    return (
                        <div className={'font-medium'}>
                            <p>{maskWholeNumber(info.getValue())}</p>
                        </div>
                    );
                },
            }),
            columnHelper.display({
                header: 'Date registered',
                cell: info => {
                    const date = DateTime.fromISO(info.row.original.created_at).toLocaleString(
                        DateTime.DATE_FULL,
                    );

                    return (
                        <div>
                            <p>{date}</p>
                        </div>
                    );
                },
            }),
            columnHelper.display({
                header: 'Status',
                cell: info => {
                    const bidder = info.row.original;
                    const status = getStatus(bidder);

                    return (
                        <div className="flex gap-4">
                            <p>{status}</p>
                            {auctionsManageBidder &&
                                (!bidder.payment_methods ||
                                    bidder.payment_methods.length === 0) && (
                                    <Tooltip.Root>
                                        <Tooltip.Trigger>
                                            <span className="sr-only">Alert</span>
                                            <FontAwesomeIcon
                                                icon={faTriangleExclamation}
                                                className="text-yellow-500"
                                            />
                                        </Tooltip.Trigger>
                                        <Tooltip.Portal>
                                            <Tooltip.Content side="right" align="center">
                                                <Tooltip.Arrow />
                                                <p>Needs payment information</p>
                                            </Tooltip.Content>
                                        </Tooltip.Portal>
                                    </Tooltip.Root>
                                )}
                        </div>
                    );
                },
            }),
            columnHelper.display({
                id: TableConstants.Actions,
                cell: info => {
                    const bidder = info.row.original;
                    return (
                        <DropdownMenu.Root>
                            <DropdownMenu.EllipsisTrigger className="float-right px-2 py-1" />
                            <DropdownMenu.Content align={'end'}>
                                <UpdateBidderNumberDialog.Root bidder={bidder}>
                                    <UpdateBidderNumberDialog.Trigger asChild>
                                        <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                            <div className={'flex items-center gap-x-2'}>
                                                <FontAwesomeIcon icon={faPenToSquare} />
                                                <p>Edit bidder #</p>
                                            </div>
                                        </DropdownMenu.Item>
                                    </UpdateBidderNumberDialog.Trigger>
                                </UpdateBidderNumberDialog.Root>
                                {auctionsManageBidder && (
                                    <DropdownMenu.LinkItem
                                        to={`/accounts/${accountId}/campaigns/${campaignId}/auction/bidders/${bidder.id}/edit`}
                                    >
                                        <div className={'flex items-center gap-x-1.5'}>
                                            <FontAwesomeIcon
                                                className={'flex size-4'}
                                                icon={faUserPen}
                                            />
                                            <p>Manage bidder</p>
                                        </div>
                                    </DropdownMenu.LinkItem>
                                )}
                                <AnonymizeBidderDialog.Root bidder={bidder}>
                                    <AnonymizeBidderDialog.Trigger asChild>
                                        <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                            <div
                                                className={`flex items-center ${
                                                    bidder.anonymous ? 'gap-x-1' : 'gap-x-3'
                                                }`}
                                            >
                                                <FontAwesomeIcon
                                                    icon={bidder.anonymous ? faEyes : faGhost}
                                                />
                                                <p>
                                                    {bidder.anonymous
                                                        ? 'Make public'
                                                        : 'Make anonymous'}
                                                </p>
                                            </div>
                                        </DropdownMenu.Item>
                                    </AnonymizeBidderDialog.Trigger>
                                </AnonymizeBidderDialog.Root>
                                {auctionsManageBidder && bidder.cant_delete && (
                                    <Tooltip.Root>
                                        <Tooltip.Trigger asChild>
                                            <DropdownMenu.Item
                                                disabled
                                                onSelect={e => e.preventDefault()}
                                            >
                                                <div className={'flex items-center gap-x-2.5'}>
                                                    <FontAwesomeIcon icon={faTrashXmark} />
                                                    Delete bidder
                                                </div>
                                            </DropdownMenu.Item>
                                        </Tooltip.Trigger>
                                        <Tooltip.Portal>
                                            <Tooltip.Content inverse>
                                                <Tooltip.Arrow inverse />
                                                <p>
                                                    This bidder cannot be deleted because they have
                                                    associated auction bids.
                                                </p>
                                            </Tooltip.Content>
                                        </Tooltip.Portal>
                                    </Tooltip.Root>
                                )}
                                {auctionsManageBidder && !bidder.cant_delete && (
                                    <DeleteBidderDialog.Root bidder={bidder}>
                                        <DeleteBidderDialog.Trigger asChild>
                                            <DropdownMenu.Item
                                                onSelect={e => e.preventDefault()}
                                                className="flex items-center gap-2"
                                            >
                                                <div className={'flex items-center gap-x-2.5'}>
                                                    <FontAwesomeIcon icon={faTrashXmark} />
                                                    <p>Delete bidder</p>
                                                </div>
                                            </DropdownMenu.Item>
                                        </DeleteBidderDialog.Trigger>
                                    </DeleteBidderDialog.Root>
                                )}
                                <BanBidderDialog.Root bidder={bidder}>
                                    <BanBidderDialog.Trigger asChild>
                                        <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                            <div className={'flex items-center gap-x-2.5'}>
                                                <FontAwesomeIcon
                                                    icon={
                                                        bidder.status === 'banned' ? faCheck : faBan
                                                    }
                                                />
                                                <p>
                                                    {bidder.status === 'banned' ? 'Unban' : 'Ban'}
                                                </p>
                                            </div>
                                        </DropdownMenu.Item>
                                    </BanBidderDialog.Trigger>
                                </BanBidderDialog.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
            }),
        ],
        [accountId, campaignId, auctionsManageBidder],
    );

    const table = useReactTable({
        data: bidders?.data ?? [],
        columns,
        pageCount: bidders?.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
            expanded,
        },
        onPaginationChange: setPagination,
        onExpandedChange: setExpanded,
        manualPagination: true,
        enableHiding: false,
        enableColumnPinning: false,
        meta: {
            renderExpandedRow: row => {
                const bidder = row.original;

                return (
                    <tr>
                        <td
                            className={'w-min bg-white px-4 pb-4'}
                            colSpan={row.getVisibleCells().length}
                        >
                            <div className="flex flex-col gap-6 p-2">
                                <CopyContactInfo
                                    email={bidder.email}
                                    phone={bidder.phone}
                                    address={bidder.address}
                                    className="grid grid-cols-2 gap-6"
                                />
                                {auctionsManageBidder && (
                                    <div className="max-w-[420px]">
                                        {(!bidder.payment_methods ||
                                            bidder.payment_methods.length === 0) &&
                                            bidder.payment_method_requested_at && (
                                                <div className={'flex flex-col gap-y-2'}>
                                                    <p
                                                        className={
                                                            'text-xs font-normal text-gray-500 sm:text-sm sm:font-medium sm:text-black'
                                                        }
                                                    >
                                                        Payment method
                                                    </p>
                                                    <div
                                                        className={`flex items-center justify-between rounded bg-gray-100 px-2.5 py-1 text-sm`}
                                                    >
                                                        <p>Requested payment method is pending.</p>
                                                    </div>
                                                </div>
                                            )}
                                    </div>
                                )}
                            </div>
                        </td>
                    </tr>
                );
            },
        },
    });

    useSyncPaginatedData(table, bidders);

    return {
        table,
        pagination,
        search,
        setSearch,
        searchTerm,
        isPending,
    };
};

const getStatus = (bidder: Bidder): ReactNode => {
    if (bidder.status === 'registration_started') {
        return '-';
    }

    if (bidder.status === 'banned') {
        return (
            <div className={'flex items-center gap-x-2 text-red-500'}>
                <FontAwesomeIcon icon={faBan} />
                Banned
            </div>
        );
    }

    if (bidder.anonymous) {
        return (
            <div className={'flex items-center gap-x-2 text-gray-500'}>
                <FontAwesomeIcon icon={faGhost} />
                Anonymous
            </div>
        );
    }

    return (
        <div className={'flex items-center gap-2'}>
            <Dot
                className={twMerge(
                    'bg-gray-400',
                    bidder.status === 'bidding' && 'bg-blue-400',
                    bidder.status === 'winner' && 'bg-green-500',
                )}
            />
            <p>{capitalize(bidder.status)}</p>
        </div>
    );
};

export { useBiddersIndex };
